import { useState } from 'react';
import { Wrench } from 'lucide-react';
import {
  ConditionLevel,
  PriorityLevel,
  PropertyRiskProfile,
  StructuralConditionLevel
} from '../types';
import InputGroup from './InputGroup';
import { formatCurrency0 } from '../utils/format';

interface PropertyRiskProfileProps {
  riskProfile: PropertyRiskProfile;
  onChange: (riskProfile: PropertyRiskProfile) => void;
}

const conditionOptions: ConditionLevel[] = ['excellent', 'good', 'watch', 'critical'];
const structuralOptions: StructuralConditionLevel[] = ['sound', 'monitor', 'moderate', 'severe'];
const priorityOptions: PriorityLevel[] = ['low', 'medium', 'high'];
const riskTone = {
  low: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  medium: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  high: 'border-rose-500/30 bg-rose-500/10 text-rose-300'
} as const;

const PropertyRiskProfileEditor = (props: PropertyRiskProfileProps) => {
  const { riskProfile, onChange } = props;
  const [expandedSystemKey, setExpandedSystemKey] = useState(null as keyof PropertyRiskProfile['majorSystems'] | null);
  const [expandedStructuralKey, setExpandedStructuralKey] = useState(null as keyof PropertyRiskProfile['structural'] | null);

  const getSystemRisk = (system: PropertyRiskProfile['majorSystems'][keyof PropertyRiskProfile['majorSystems']]) => {
    const remainingLife = Math.max(0, system.expectedLife - system.age);
    if (system.condition === 'critical' || system.priority === 'high' || remainingLife <= 2) return 'high';
    if (system.condition === 'watch' || system.priority === 'medium' || remainingLife <= 7) return 'medium';
    return 'low';
  };

  const getStructuralRisk = (item: PropertyRiskProfile['structural'][keyof PropertyRiskProfile['structural']]) => {
    if (item.condition === 'severe') return 'high';
    if (item.condition === 'moderate' || item.condition === 'monitor') return 'medium';
    return 'low';
  };

  const majorRiskSummary = Object.values(riskProfile.majorSystems).reduce((acc, system) => {
    const risk = getSystemRisk(system);
    if (risk === 'low') acc.healthy += 1;
    else acc.atRisk += 1;
    return acc;
  }, { atRisk: 0, healthy: 0 });

  const structuralRiskSummary = Object.values(riskProfile.structural).reduce((acc, item) => {
    const risk = getStructuralRisk(item);
    if (risk === 'low') acc.healthy += 1;
    else acc.atRisk += 1;
    return acc;
  }, { atRisk: 0, healthy: 0 });

  const updateMajorSystem = (key: keyof PropertyRiskProfile['majorSystems'], field: string, value: string | number) => {
    onChange({
      ...riskProfile,
      majorSystems: {
        ...riskProfile.majorSystems,
        [key]: {
          ...riskProfile.majorSystems[key],
          [field]: value
        }
      }
    });
  };

  const updateStructural = (key: keyof PropertyRiskProfile['structural'], field: string, value: string | number) => {
    onChange({
      ...riskProfile,
      structural: {
        ...riskProfile.structural,
        [key]: {
          ...riskProfile.structural[key],
          [field]: value
        }
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
        <div className="mb-6">
          <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Major Systems</h3>
          <p className="text-xs text-slate-500 mt-2">Capture aging systems, replacement budgets, and urgency after the property is already saved.</p>
        </div>
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-amber-300">
            {majorRiskSummary.atRisk} systems at risk
          </div>
          <div className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-300">
            {majorRiskSummary.healthy} systems healthy
          </div>
        </div>
        <div className="space-y-4">
          {Object.entries(riskProfile.majorSystems).map(([key, system]) => (
            <div key={key} className="rounded-2xl border border-slate-700/70 bg-slate-950/60 overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedSystemKey((current: keyof PropertyRiskProfile['majorSystems'] | null) => current === key ? null : key as keyof PropertyRiskProfile['majorSystems'])}
                className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-slate-900/60"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-800">
                    <Wrench size={15} className="text-emerald-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-black text-white">
                      {system.label} <span className="font-medium text-slate-400">• {Math.max(0, system.expectedLife - system.age)} yrs left • {formatCurrency0(system.replacementCost)}</span>
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${riskTone[getSystemRisk(system)]}`}>
                    {getSystemRisk(system)} risk
                  </span>
                  <span className="text-xs font-black uppercase tracking-widest text-slate-500">
                    {expandedSystemKey === key ? 'Hide' : 'Edit'}
                  </span>
                </div>
              </button>

              {expandedSystemKey === key && (
                <div className="border-t border-slate-700/70 px-4 py-4">
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                    <InputGroup label="Age" value={system.age} min={0} max={50} step={1} onChange={(v) => updateMajorSystem(key as keyof PropertyRiskProfile['majorSystems'], 'age', v)} />
                    <InputGroup label="Expected Life" value={system.expectedLife} min={1} max={50} step={1} onChange={(v) => updateMajorSystem(key as keyof PropertyRiskProfile['majorSystems'], 'expectedLife', v)} />
                    <InputGroup label="Replacement Cost" value={system.replacementCost} min={0} max={100000} step={250} type="currency" onChange={(v) => updateMajorSystem(key as keyof PropertyRiskProfile['majorSystems'], 'replacementCost', v)} />
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block">Condition</label>
                      <select value={system.condition} onChange={(e: any) => updateMajorSystem(key as keyof PropertyRiskProfile['majorSystems'], 'condition', (e.target as HTMLSelectElement).value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-sm font-bold text-slate-100 outline-none focus:border-emerald-500">
                        {conditionOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block">Priority</label>
                      <select value={system.priority} onChange={(e: any) => updateMajorSystem(key as keyof PropertyRiskProfile['majorSystems'], 'priority', (e.target as HTMLSelectElement).value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-sm font-bold text-slate-100 outline-none focus:border-emerald-500">
                        {priorityOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </div>
                  </div>
                  <textarea value={system.notes} onChange={(e: any) => updateMajorSystem(key as keyof PropertyRiskProfile['majorSystems'], 'notes', (e.target as HTMLTextAreaElement).value)} className="mt-4 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm text-slate-200 outline-none focus:border-emerald-500 min-h-[84px]" placeholder="Inspection notes, contractor quote status, or maintenance history" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
        <div className="mb-6">
          <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Structural Conditions</h3>
          <p className="text-xs text-slate-500 mt-2">These are not cosmetic. Structural and systems condition feeds the risk score, reserve recommendations, and near-term capital exposure.</p>
        </div>
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-amber-300">
            {structuralRiskSummary.atRisk} systems at risk
          </div>
          <div className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-300">
            {structuralRiskSummary.healthy} systems healthy
          </div>
        </div>
        <div className="space-y-3">
          {Object.entries(riskProfile.structural).map(([key, item]) => (
            <div key={key} className="rounded-2xl border border-slate-700/70 bg-slate-950/60 overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedStructuralKey((current: keyof PropertyRiskProfile['structural'] | null) => current === key ? null : key as keyof PropertyRiskProfile['structural'])}
                className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-slate-900/60"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-black text-white">
                    {item.label} <span className="font-medium text-slate-400">• {formatCurrency0(item.estimatedCost)} exposure</span>
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${riskTone[getStructuralRisk(item)]}`}>
                    {getStructuralRisk(item)} risk
                  </span>
                  <span className="text-xs font-black uppercase tracking-widest text-slate-500">
                    {expandedStructuralKey === key ? 'Hide' : 'Edit'}
                  </span>
                </div>
              </button>

              {expandedStructuralKey === key && (
                <div className="border-t border-slate-700/70 px-4 py-4">
                  <div className="space-y-4">
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block">Condition</label>
                      <select value={item.condition} onChange={(e: any) => updateStructural(key as keyof PropertyRiskProfile['structural'], 'condition', (e.target as HTMLSelectElement).value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-sm font-bold text-slate-100 outline-none focus:border-emerald-500">
                        {structuralOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </div>
                    <InputGroup label="Estimated Exposure" value={item.estimatedCost} min={0} max={50000} step={250} type="currency" onChange={(v) => updateStructural(key as keyof PropertyRiskProfile['structural'], 'estimatedCost', v)} />
                    <textarea value={item.notes} onChange={(e: any) => updateStructural(key as keyof PropertyRiskProfile['structural'], 'notes', (e.target as HTMLTextAreaElement).value)} className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm text-slate-200 outline-none focus:border-emerald-500 min-h-[80px]" placeholder="Describe active concerns, inspection findings, or likely remediation scope" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
        <div className="mb-6">
          <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Baseline Notes</h3>
          <p className="text-xs text-slate-500 mt-2">Keep this section focused on the asset's current physical condition. Future repairs, operating changes, and stress tests now live in scenarios so there is only one simulation path.</p>
        </div>
        <textarea value={riskProfile.notes} onChange={(e: any) => onChange({ ...riskProfile, notes: (e.target as HTMLTextAreaElement).value })} className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm text-slate-200 outline-none focus:border-emerald-500 min-h-[96px]" placeholder="Risk notes, inspection reminders, or operating context for future review" />
      </div>
    </div>
  );
};

export default PropertyRiskProfileEditor;
